/**
 * Aufgaben „Heute“ ([V] E-f, [TP4] 7.1; Anhang A.tasks): Eigentum TP4 (`relationship-building`), andere
 * Module liefern Kandidaten über `TaskCandidateProvider` und setzen Aufgaben nur über
 * `TaskService.markDoneFromSource(...)` auf erledigt.
 *
 * - `TaskKind`: 13 Arten inkl. `content_work`, `account`, `identity_review`, `manual` (E-f).
 * - `ActionSpec`: nur zulässige Paare aus Aktionsart und deklarierter Klasse (Reaktion nie werblich).
 * - `CANDIDATE_KEY_PATTERNS`: Format des `candidateKey` je Art (R-24); identisch mit der `actionIdentity`
 *   der Buchungsquelle (E-p12).
 * - Ownership-Regeln der Port-Methoden: eigene, nicht (mehr) vorhandene Quelle → idempotentes No-op;
 *   fremde Quelle → `DomainFailure('NOT_FOUND')`.
 */
import { z } from 'zod';
import type { DbTx, DomainContext, DomainResult, PersonId, UserId } from './common.js';
import type { HeldLocks } from './locks.js';
import type { OperationRef } from './operations.js';

export const TaskKind = z.enum([
  'reply',
  'follow_up',
  'post_publish',
  'review',
  'privacy_notice',
  'warmup_step',
  'sequence_step',
  'comment_opportunity',
  'nurture',
  'content_work',
  'account',
  'identity_review',
  'manual',
]);
export type TaskKind = z.infer<typeof TaskKind>;

export const ActionKind = z.enum([
  'none',
  'reaction',
  'comment',
  'invitation',
  'first_message',
  'message',
  'post',
]);
export const CommunicationClass = z.enum([
  'none',
  'invitation_plain',
  'personal',
  'reply_to_inbound',
  'privacy_notice',
  'promotional',
]);

export const ActionSpec = z.discriminatedUnion('actionKind', [
  z.object({ actionKind: z.literal('none'), declaredClass: z.literal('none') }),
  z.object({ actionKind: z.literal('reaction'), declaredClass: z.literal('none') }),
  z.object({ actionKind: z.literal('comment'), declaredClass: z.literal('none') }),
  z.object({ actionKind: z.literal('post'), declaredClass: z.literal('none') }),
  z.object({
    actionKind: z.literal('invitation'),
    declaredClass: z.enum(['invitation_plain', 'personal', 'promotional']),
  }),
  z.object({
    actionKind: z.literal('first_message'),
    declaredClass: z.enum(['personal', 'privacy_notice', 'promotional']),
  }),
  z.object({
    actionKind: z.literal('message'),
    declaredClass: z.enum(['personal', 'privacy_notice', 'promotional']),
  }),
]);

export const TaskCandidate = z.object({
  candidateKey: z.string().min(3).max(200),
  providerId: z.string(),
  kind: TaskKind,
  personId: z.uuid().nullable(),
  connectedAccountId: z.uuid().nullable(),
  action: ActionSpec,
  dueAt: z.iso.datetime(),
  expiresAt: z.iso.datetime().nullable(),
  sourceRef: z.object({ type: z.string(), id: z.string() }),
  facts: z.object({
    waitingHours: z.number().nonnegative().optional(),
    overdueDays: z.number().nonnegative().optional(),
    postAgeHours: z.number().nonnegative().optional(),
    daysSinceLastExchange: z.number().nonnegative().optional(),
    strengthDrop28d: z.number().optional(),
    stepIndex: z.number().int().optional(),
    stepCount: z.number().int().optional(),
    label: z.string().max(120).optional(),
    intent: z.string().max(40).optional(),
  }),
  link: z.object({
    type: z.enum([
      'open_conversation',
      'open_profile',
      'open_post',
      'open_share',
      'open_internal',
      'decision',
      'none',
    ]),
    url: z.url().nullable(),
    /**
     * A-C1 b: Pflicht bei `type = 'open_internal'` (die Vertragssuite `TaskCandidateProvider` prüft die Regel).
     * Gegenüber dem Vertragsmuster verschärft: kein zweiter führender Schrägstrich (`//host` wäre protokollrelativ
     * und verließe die eigene Origin).
     */
    internalPath: z
      .string()
      .regex(/^\/(?!\/)[a-z0-9/#_-]{0,120}$/)
      .exactOptional(),
    draftRef: z
      .object({
        type: z.enum([
          'tp3.draft',
          'tp5.post_draft',
          'tp5.comment_draft',
          'tp2.warmup_text',
          'tp4.template',
          'operation',
        ]),
        id: z.string(),
      })
      .nullable(),
    conversationRef: z.uuid().nullable(),
  }),
});
export type TaskCandidate = z.infer<typeof TaskCandidate>;

export interface TaskCandidateProvider {
  id: string;
  kinds: readonly TaskKind[];
  collect(ctx: {
    userId: UserId;
    now: Date;
    planDate: string;
    signal: AbortSignal;
  }): Promise<TaskCandidate[]>;
}

export interface TaskService {
  createForOperation(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    op: OperationRef,
  ): Promise<string>;
  removeForOperation(tx: DbTx, held: HeldLocks, ctx: DomainContext, opId: string): Promise<void>;
  markDoneFromSource(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: { sourceRef: { type: string; id: string }; source: 'changelog' | 'user' },
  ): Promise<void>;
  markOpened(tx: DbTx, held: HeldLocks, ctx: DomainContext, taskId: string): Promise<void>;
  /** nur lesend (R-33) */
  planLocksForSource(
    tx: DbTx,
    ctx: DomainContext,
    a: { sourceRef: { type: string; id: string } },
  ): Promise<string[]>;
  removeForSource(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: { sourceRef: { type: string; id: string }; reason: string },
  ): Promise<void>;
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

/** Format des `candidateKey` je Aufgabenart (R-24, [TP4] 7.1.1). */
export const CANDIDATE_KEY_PATTERNS: Readonly<Record<TaskKind, RegExp>> = {
  reply: new RegExp(`^reply:${UUID}$`),
  follow_up: new RegExp(`^followup:${UUID}:\\d+$`),
  post_publish: new RegExp(`^post:${UUID}:\\d+$`),
  review: /^review:[a-z_]+:[A-Za-z0-9:_-]+$/,
  privacy_notice: new RegExp(`^privacy:${UUID}(:\\d+)?$`),
  warmup_step: new RegExp(`^warmup:${UUID}$`),
  sequence_step: new RegExp(`^seq:${UUID}:\\d+$`),
  comment_opportunity: new RegExp(`^comment:${UUID}$`),
  nurture: new RegExp(`^nurture:${UUID}:\\d{4}-Q[1-4]$`),
  content_work: /^tp5:[a-z_]+:[A-Za-z0-9_-]+$/,
  account: /^account:[A-Za-z0-9:_-]+$/,
  identity_review: new RegExp(`^identity_review:${UUID}$`),
  manual: new RegExp(`^manual:${UUID}$`),
};

export interface HandoffDelegate {
  draftRefType: 'tp3.draft' | 'tp5.comment_draft' | 'tp2.warmup_text' | 'tp4.template';
  planLocks(tx: DbTx, ctx: DomainContext, draftRefId: string): Promise<string[]>;
  handoff(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: { taskId: string; draftRefId: string; confirmations?: { not_promotional?: true } },
  ): Promise<DomainResult<{ url: string | null; clipboardText: string; art14Appended: boolean }>>;
}

export interface MeetingSink {
  record(
    tx: DbTx,
    held: HeldLocks,
    ctx: DomainContext,
    a: {
      personId: PersonId;
      conversationId?: string;
      agreedAt: string;
      source: 'tp3_suggestion_confirmed' | 'manual';
    },
  ): Promise<void>;
}
